import { mapGetters } from 'vuex';
import { normalizeErrorMessage } from '@/utils/error-message';
import { ROLE_ADMIN_MITRA, ROLE_ADMIN_PJO, ROLE_HSE } from '@/constants/mitraRole';
import mitraCourseListFilter from '@/mixins/mitraCourseListFilter';
import {
  APPROVAL_STEP_SLUG_OPTIONS,
  COURSE_TYPE_BOOLEAN_OPTIONS,
  ENROLLMENT_COURSE_STATUS_OPTIONS
} from '@/constants/mitraFilters';

export default {
  name: 'MitraEnrollmentReport',
  mixins: [mitraCourseListFilter],
  data() {
    return {
      selectedCourse: null,
      selectedBatch: null,
      selectedBatchIndex: 0,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 50, 100],
      filterModalId: 'modal-filter-mitra-enrollment-report',
      statusQueryKey: 'status',
      filters: {
        search: (this.$route && this.$route.query && this.$route.query.search) || '',
        company: null,
        status: null,
        approval_step: null,
        course_type: null
      },
      statusOptions: ENROLLMENT_COURSE_STATUS_OPTIONS,
      approvalStepOptions: APPROVAL_STEP_SLUG_OPTIONS,
      courseTypeOptions: COURSE_TYPE_BOOLEAN_OPTIONS,
      companyOptions: [],
      companiesLoading: false,
      selectedPeriod: null,
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      targetTrainingEventId: null,
      hasOpenedDeepLink: false
    };
  },
  computed: {
    ...mapGetters('mitraEnrollmentReport', ['courses', 'pagination', 'loading', 'error', 'summary', 'summaryLoading']),
    ...mapGetters('idpPeriod', ['IdpPeriod']),
    ...mapGetters('users', ['profileData']),
    
    // Period dropdown options from store
    periodOptions() {
      const data = this.IdpPeriod;

      const list = data?.data;
      if (!list || !list.length) return [];

      return list.map(p => ({
        label: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id
      }));
    },
    
    isLoading() {
      return this.loading;
    },

    isHSERole() {
      const roles = [
        this.profileData?.role_id,
        ...(this.profileData?.roles_uenroll || []),
        ...(this.profileData?.roles_uenrol || []),
      ].filter(Boolean);
      return roles.includes(ROLE_HSE) && !roles.includes(ROLE_ADMIN_PJO) && !roles.includes(ROLE_ADMIN_MITRA);
    },
    
    filteredCourses() {
      let filtered = this.courses || [];
      
      if (this.filters.search) {
        const searchLower = this.filters.search.toLowerCase();
        filtered = filtered.filter(course => 
          course.title.toLowerCase().includes(searchLower) ||
          course.description?.toLowerCase().includes(searchLower) ||
          course.provider_name?.toLowerCase().includes(searchLower)
        );
      }
      
      return filtered;
    },
    
    paginatedCourses() {
      return this.courses || [];
    },
    
    totalItems() {
      return this.pagination?.total || 0;
    },
    
    totalPages() {
      return this.pagination?.totalPage || 0;
    },
    
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages;
    }
  },
  
  watch: {
    'filters.search'() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    'filters.company'() {
      this.currentPage = 1;
      this.fetchSummary();
      this.fetchCourses();
    },
    selectedPeriod() {
      this.fetchSummary();
      this.fetchCourses();
    },
    currentPage() {
      this.fetchCourses();
    },
    profileData: {
      immediate: true,
      handler() {
        if (this.isHSERole && !this.companyOptions.length) {
          this.fetchCompanies();
        }
      }
    }
  },
  
  methods: {
    async fetchCompanies() {
      if (this.companiesLoading) return;

      this.companiesLoading = true;
      try {
        const response = await this.$store.dispatch('mitraTnaReport/getCompanies', {
          limit: 1000,
          is_external: true
        });
        this.companyOptions = response?.data || [];
      } catch (error) {
        console.error('Error fetching companies:', error);
      } finally {
        this.companiesLoading = false;
      }
    },

    applyCompanyFilter(params) {
      if (this.filters.company) {
        params.company = this.filters.company;
      }
      return params;
    },

    async fetchCourses() {
      try {
        const params = {
          page: this.currentPage,
          limit: this.perPage
        };
        
        if (this.filters.search) {
          params.search = this.filters.search;
        }
        
        if (this.selectedPeriod) {
          params.idp_period = this.selectedPeriod;
        }

        if (this.targetTrainingEventId) {
          params.training_event_id = this.targetTrainingEventId;
        }

        this.applyCompanyFilter(params);
        this.applyCourseListFilterParams(params);
        
        const courses = await this.$store.dispatch('mitraEnrollmentReport/getCourses', params);
        this.openDeepLinkTarget(courses);
      } catch (error) {
        console.error('Error fetching courses:', error);
        this.$swal({
          title: 'Gagal Memuat Data',
          text: normalizeErrorMessage(error, 'Data pelatihan belum berhasil dimuat. Silakan coba lagi.'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      }
    },
    
    async fetchSummary() {
      if (!this.selectedPeriod) return;
      
      try {
        const params = {
          idp_period: this.selectedPeriod
        };
        this.applyCompanyFilter(params);
        await this.$store.dispatch('mitraEnrollmentReport/getSummary', params);
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    },
    
    async viewCourseDetail(course) {
      this.selectedCourse = course;
      // Show batch approval list modal - fetching will be handled by modal's @shown event
      this.$bvModal.show('modal-batch-approval-list');
    },

    openDeepLinkTarget(courses = this.courses) {
      if (this.hasOpenedDeepLink || !this.targetTrainingEventId) return;

      const targetCourseId = this.$route?.query?.course_id;
      const course = (courses || []).find(item => {
        if (!targetCourseId) return true;
        return String(item.id) === String(targetCourseId);
      });

      if (!course) return;

      this.hasOpenedDeepLink = true;
      this.viewCourseDetail(course);
    },
    
    handleBatchSelected({ batch, index }) {
      this.selectedBatch = batch;
      this.selectedBatchIndex = index;
      
      // Show participant approval modal
      this.$bvModal.show('modal-participant-approval');
    },
    
    async refreshBatches() {
      // Re-fetch batches after approval
      try {
        const params = {};
        if (this.selectedPeriod) {
          params.idp_period = this.selectedPeriod;
        }
        this.applyCompanyFilter(params);

        await this.$store.dispatch('mitraEnrollmentReport/getBatchesApproval', {
          courseId: this.selectedCourse.id,
          params
        });
      } catch (error) {
        console.error('Error refreshing batches:', error);
      }
    },
    
    changePage(page) {
      if (page === '...' || page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchCourses();
    },
    
    clearFilters() {
      this.filters = {
        search: '',
        company: null
      };
      this.currentPage = 1;
    },
    
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },

    handleImageProviderError(event) {
      event.target.src = 'https://dummyimage.com/16x16/ffffff/000000&text=Provider+title';
    },
    
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type.includes('certification') && !type.includes('semi')) {
        return 'fe fe-file-text';
      } else if (type.includes('semi')) {
        return 'fe fe-shield';
      } else if (type.includes('upskilling')) {
        return 'fe fe-trending-up';
      } else if (type.includes('reskilling')) {
        return 'fe fe-refresh-cw';
      } else if (type.includes('mandatory')) {
        return 'fe fe-alert-circle';
      } else {
        return 'fe fe-book-open';
      }
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        Schedule: 'badge bg-success',
        'No Schedule': 'badge bg-warning',
        Completed: 'badge bg-info',
        Cancelled: 'badge bg-danger',
        SCHEDULE: 'badge bg-success',
        'NO SCHEDULE': 'badge bg-warning',
        COMPLETED: 'badge bg-info',
        CANCELLED: 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },
    
    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return '';
      
      const fmt = (dateStr) => {
        const d = new Date(dateStr);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };
      
      return `${fmt(startDate)} - ${fmt(endDate)}`;
    }
  },
  
  async mounted() {
    console.log('✅ Mitra Enrollment Report page mounted');
    this.targetTrainingEventId = this.$route?.query?.training_event_id || null;
    
    // Fetch IDP periods first
    try {
      const result = await this.$store.dispatch('idpPeriod/getIdpPeriod');
      console.log('[idpPeriod] dispatch result:', result);

      // Set period with vstatus 'open' as default if available
      const periods = this.IdpPeriod?.data || [];
      const openPeriod = periods.find(p => p.vstatus === 'open');
      
      if (openPeriod) {
        this.selectedPeriod = openPeriod.id;
      } else if (this.periodOptions.length > 0) {
        // Fallback to first period if no open period found
        this.selectedPeriod = this.periodOptions[0].value;
      }
    } catch (error) {
      console.error('Error fetching idp periods:', error);
    }
    
    // Fetch courses
    this.fetchCourses();
  }
};
